import {
  ErrorMessage,
  FieldArray,
  type FieldArrayRenderProps,
  type FormikValues,
  useFormikContext,
} from "formik";
import { get } from "lodash";
import React, { type ComponentPropsWithoutRef, useEffect } from "react";
import PlusIcon from "shared/components/icons/sign/PlusIcon";
import PillarFormButton from "shared/components/pillar-form/components/PillarFormButton";
import { type PillarFormInputWrapperProps } from "shared/components/pillar-form/components/PillarFormInputWrapper";

export type PillarFormDynamicArrayProps = PillarFormInputWrapperProps &
  Omit<ComponentPropsWithoutRef<"input">, "id" | "name" | "value" | "type"> & {
    minItems: number;
    maxItems: number;
    // defaultItem is used to fill initial value or to push item when the new one is created
    defaultItem: Record<string, unknown>;
    itemContainerClassName?: string;
    itemWrapperClassName?: string;
    hideRemoveButton?: boolean;
    hideAddButton?: boolean;
    placeholderClassName?: string;
  };

const PillarFormDynamicArray = ({
  name,
  title,
  label,
  labelClassName,
  className,
  children,
  minItems,
  testid,
  maxItems,
  defaultItem,
  itemContainerClassName,
  itemWrapperClassName,
  hideRemoveButton = false,
  hideAddButton = false,
  placeholderClassName,
  placeholder,
  ...props
}: PillarFormDynamicArrayProps) => {
  const { values, errors, setFieldValue } = useFormikContext<FormikValues>();
  const value = get(values, name) || [];

  useEffect(() => {
    if (!value?.length && minItems > 0) {
      setFieldValue(name, Array(minItems).fill(defaultItem));
    }

    if (value?.length < minItems) {
      setFieldValue(name, [
        ...value,
        ...Array(minItems - value.length).fill(defaultItem),
      ]);
    }
  }, [value, minItems, defaultItem, name, setFieldValue]);

  const renderChildren = (index: number, arrayMethods: FieldArrayRenderProps) =>
    React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          ...child.props,
          name: `${name}[${index}].${child.props.name}`,
          index: index,
          arrayMethods,
        });
      }
      return child;
    });

  return (
    <div className={`${className ?? ""}`}>
      {label && (
        <label
          className={`mb-0.5 inline-block text-base ${
            labelClassName ?? "text-filter-label"
          }`}
        >
          {label}:
        </label>
      )}
      <FieldArray name={name}>
        {(arrayMethods) => (
          <div className={`flex flex-col gap-y-2 ${label ? "pl-1" : ""}`}>
            {value.map((_: Array<Record<string, unknown>>, index: number) => (
              <div
                key={index}
                className={`${itemContainerClassName ?? "flex gap-x-3"}`}
              >
                <div className={`${itemWrapperClassName ?? "w-full"}`}>
                  {children && renderChildren(index, arrayMethods)}
                </div>
                {!hideRemoveButton && value.length > minItems && (
                  <PillarFormButton
                    testid={`${name}-remove-${index}`}
                    onClick={() => arrayMethods.remove(index)}
                    className="button-regular-destructive-filled h-4.5 mt-2.75"
                  >
                    Remove
                  </PillarFormButton>
                )}
              </div>
            ))}
            {!value.length && placeholder && (
              <p
                className={`${
                  placeholderClassName
                    ? placeholderClassName
                    : "text-center text-xl"
                }`}
              >
                {placeholder}
              </p>
            )}
            {!Array.isArray(errors[name]) && !!errors[name] && (
              <ErrorMessage
                name={name}
                component="span"
                className="text-danger-small"
              />
            )}
            {!hideAddButton && value.length < maxItems && (
              <PillarFormButton
                onClick={() => arrayMethods.push(defaultItem)}
                className="w-fit h-5"
                testid={`add-${testid}`}
              >
                <PlusIcon className="stroke-neutral-light" />
                <span className="text-neutral-light text-sm">Add {title}</span>
              </PillarFormButton>
            )}
          </div>
        )}
      </FieldArray>
    </div>
  );
};

export default PillarFormDynamicArray;
